// Project-level Take-offs compare matrix view-model.
//
// This is a PROJECT-LEVEL view (every apartment type as a column + a Job-total
// column), not a single-apartment view. It has EXACTLY TWO row groups:
//
//   1. Take-offs  — what was measured in the property, derived from the project
//                   `takeoffRows[]` (fan coils, UFH zones, bedrooms, ...).
//   2. Controls   — the control kit we provide, derived from each apartment
//                   solution's controller / device / infrastructure counts.
//
// Cabling (Modbus / RS-485 / M-Bus / metering-bus / daisy-chain, etc.) is NEVER
// a row here — it is separate, secondary infrastructure listed elsewhere. The
// `isCablingDescriptor` predicate below is the single source of truth for what
// counts as cabling, and is also reused by the legacy spec-matrix derivation so
// the two views agree on the exclusion.

/** A project take-off row (one per apartment type). Structurally compatible
 * with the web fixture's `takeoffRows[]` and the richer engine take-off row. */
export interface CompareTakeoffRow {
  apartmentTypeId: string;
  rawLabel?: string;
  /** Number of homes of this apartment type — the column quantity multiplier. */
  quantity: number;
  beds?: number;
  fcuCount?: number;
  towelRailCount?: number;
  wufhZoneCount?: number;
  eufhZoneCount?: number;
  standaloneHeatOnlyZoneCount?: number;
  thermostatUiPointCount?: number;
  centralControlScreenCount?: number;
  hiuCount?: number;
  ciuCount?: number;
  althermaCount?: number;
}

interface CompareControlItem {
  role?: string;
  itemKind?: string;
  productPartNo?: string;
  partNo?: string;
  name?: string;
  supplySource?: string;
  quantity?: number;
}

type CompareControlCollection = "controllers" | "devices" | "infrastructure";

interface CompareControlCandidate {
  collection: CompareControlCollection;
  item: CompareControlItem;
}

/** A per-type apartment solution. Structurally compatible with
 * `ApartmentSolutionForDisplay` — only the fields this view-model reads. */
export interface CompareControlSolution {
  apartmentTypeId: string;
  quantity?: number;
  controllers: readonly CompareControlItem[];
  devices: readonly CompareControlItem[];
  infrastructure: readonly CompareControlItem[];
}

export interface CompareMatrixColumn {
  apartmentTypeId: string;
  name: string;
  sub?: string;
  /** Homes of this apartment type — used as the × quantity multiplier. */
  homes: number;
}

export interface CompareMatrixCell {
  apartmentTypeId: string;
  /** Per-type (per-home) quantity. */
  value: number;
  /** The generated value before a user-edited override was applied. */
  baseValue?: number;
  isUserEdited?: boolean;
  overrideReason?: string;
  overrideUpdatedAt?: string;
  overrideUpdatedBy?: "user";
}

export interface CompareMatrixRow {
  id: string;
  /** Stable grouping key (take-off field key, or control role / itemKind). */
  kind: string;
  label: string;
  note?: string;
  cells: CompareMatrixCell[];
  /** Whole-job quantity for this row: sum over types of value x homes. */
  jobTotal: number;
}

export type CompareGroupId = "takeoffs" | "controls";

export interface CompareMatrixGroup {
  id: CompareGroupId;
  label: string;
  meta: string;
  rows: CompareMatrixRow[];
  /** Per-column total (sum of per-type values down the column). */
  totals: CompareMatrixCell[];
  /** Whole-job grand total for the group (sum of row job totals). */
  jobTotal: number;
}

export interface CompareMatrixAnomaly {
  id: string;
  groupId: CompareGroupId;
  rowId: string;
  rowLabel: string;
  apartmentTypeId: string;
  apartmentTypeName: string;
  value: number;
  typicalValue: number;
  direction: "high" | "low" | "only";
  reason: string;
}

export interface CompareMatrixViewModel {
  projectId?: string;
  status: "ready" | "empty";
  columns: CompareMatrixColumn[];
  jobTotalLabel: string;
  jobTotalSub: string;
  groups: CompareMatrixGroup[];
  anomalies: CompareMatrixAnomaly[];
  dataGaps: string[];
}

export interface BuildCompareMatrixInput {
  takeoffRows: readonly CompareTakeoffRow[];
  solutions: readonly CompareControlSolution[];
  projectId?: string;
  overrides?: readonly CompareMatrixCellOverride[];
}

export interface CompareMatrixCellOverride {
  groupId: CompareGroupId;
  kind: string;
  apartmentTypeId: string;
  value: number;
  reason?: string;
  updatedAt?: string;
  updatedBy?: "user";
}

export const compareMatrixOverrideKey = (
  groupId: CompareGroupId,
  kind: string,
  apartmentTypeId: string
): string => `${groupId}::${kind}::${apartmentTypeId}`;

// --- Cabling exclusion (single source of truth) -----------------------------

const CABLING_PATTERN =
  /(modbus|rs[-_\s]?485|wireless[-_\s]?m[-_\s]?bus|\bm[-_\s]?bus\b|mbus|daisy|cabl|cable|metering[-_\s]?bus|busbar|\bbus\b)/i;

/** True when the supplied descriptor names cabling / a metering bus rather than
 * a control device. Used to keep cabling out of the Controls group (and out of
 * the legacy spec matrix). */
export const isCablingDescriptor = (descriptor: string): boolean =>
  CABLING_PATTERN.test(descriptor);

const isCablingControlItem = (item: CompareControlItem): boolean =>
  isCablingDescriptor(
    [item.itemKind, item.role, item.productPartNo, item.partNo, item.name]
      .filter(Boolean)
      .join(" ")
  );

// --- Take-off rows ----------------------------------------------------------

const TAKEOFF_FIELDS: { key: keyof CompareTakeoffRow; label: string }[] = [
  { key: "fcuCount", label: "Fan coils" },
  { key: "wufhZoneCount", label: "Wet UFH zones" },
  { key: "eufhZoneCount", label: "Electric UFH zones" },
  { key: "standaloneHeatOnlyZoneCount", label: "Heat-only zones" },
  { key: "towelRailCount", label: "Towel rails" },
  { key: "thermostatUiPointCount", label: "Thermostat points" },
  { key: "centralControlScreenCount", label: "Central control screens" },
  { key: "hiuCount", label: "Heat interface units" },
  { key: "ciuCount", label: "Consumer interface units" },
  { key: "althermaCount", label: "Altherma units" },
  { key: "beds", label: "Bedrooms" }
];

// --- Control row labels / ordering ------------------------------------------

const CONTROL_LABELS: Record<string, string> = {
  fcu_primary: "Fan-coil controllers",
  heat_only_710: "Heat-only controllers",
  mz_overflow: "Overflow zone controllers",
  local_ui_ts4: "Local room screens",
  central_control_screen_ts7: "Central control screens",
  control_temperature_sensor: "Control sensors",
  ufh_limit_sensor_active: "UFH limit sensors (active)",
  ufh_limit_sensor_spare: "UFH limit sensors (spare)",
  supply_air_off_coil_sensor: "Supply-air sensors",
  io_module: "I/O modules & relays",
  relay: "Load relays",
  netmaster: "Netmaster",
  network_switch: "Network switch",
  panel: "Enclosure / panel",
  edge_compute: "Edge gateway",
  edge_enclosure: "Edge gateway",
  cloud_service: "Edge gateway",
  other: "Unclassified control kit",
  unknown: "Unclassified control kit",
  miscellaneous: "Unclassified control kit",
  infrastructure: "Unclassified infrastructure kit"
};

const CONTROL_ORDER: string[] = [
  "fcu_primary",
  "local_ui_ts4",
  "central_control_screen_ts7",
  "control_temperature_sensor",
  "io_module",
  "netmaster",
  "panel",
  "edge_compute"
];

const CONTROL_PARENT_KIND_BY_KIND: Record<string, string> = {
  heat_only_710: "fcu_primary",
  mz_overflow: "fcu_primary",
  ufh_limit_sensor_active: "control_temperature_sensor",
  ufh_limit_sensor_spare: "control_temperature_sensor",
  supply_air_off_coil_sensor: "control_temperature_sensor",
  relay: "io_module",
  load_relay_module: "io_module",
  edge_network_switch: "edge_compute",
  network_switch: "edge_compute",
  edge_enclosure: "edge_compute",
  cloud_service: "edge_compute",
  edge_cloud_service: "edge_compute",
  control_panel_enclosure: "panel",
  power_supply: "panel"
};

const CENTRAL_SCREEN_PRODUCT_PART_NOS = new Set(["TP-UI-TS-7", "TP-CCS-7-W"]);

const isCentralScreenProduct = (item: CompareControlItem): boolean =>
  [item.productPartNo, item.partNo]
    .filter((partNo): partNo is string => typeof partNo === "string")
    .some((partNo) => CENTRAL_SCREEN_PRODUCT_PART_NOS.has(partNo.toUpperCase()));

const humanise = (value: string): string =>
  value
    .replaceAll("_", " ")
    .replaceAll("-", " ")
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase());

const controlLabel = (kind: string): string => CONTROL_LABELS[kind] ?? humanise(kind);

const CONTROL_ROW_NOTES: Record<string, string> = {
  other: "Items arrived without a recognised controls role; review before quoting.",
  unknown: "Items arrived without a recognised controls role; review before quoting.",
  miscellaneous:
    "Items arrived without a recognised controls role; review before quoting.",
  infrastructure:
    "Infrastructure items that were not classified into a named controls role."
};

const controlRowNote = (kind: string): string | undefined => CONTROL_ROW_NOTES[kind];

const controlKindOf = (
  collection: CompareControlCollection,
  item: CompareControlItem
): string => {
  if (isCentralScreenProduct(item)) {
    return "central_control_screen_ts7";
  }

  if (collection === "infrastructure") {
    return item.itemKind ?? item.role ?? item.productPartNo ?? "infrastructure";
  }

  return item.role ?? item.itemKind ?? item.productPartNo ?? collection;
};

const controlParentKindOf = (
  collection: CompareControlCollection,
  item: CompareControlItem
): string => {
  const kind = controlKindOf(collection, item);

  return CONTROL_PARENT_KIND_BY_KIND[kind] ?? kind;
};

const GENERIC_CONTROL_KINDS = new Set([
  "other",
  "unknown",
  "miscellaneous",
  "infrastructure"
]);

const normalizedProductPartNo = (item: CompareControlItem): string | null => {
  const partNo = item.productPartNo ?? item.partNo;

  return typeof partNo === "string" && partNo.trim().length > 0
    ? partNo.trim().toUpperCase()
    : null;
};

const normalizedSupplySource = (
  collection: CompareControlCollection,
  item: CompareControlItem
): string => {
  if (collection !== "infrastructure") {
    const descriptor = `${item.productPartNo ?? item.partNo ?? ""} ${
      item.role ?? ""
    }`.toLowerCase();

    return descriptor.includes("mbus") || /by[-_\s]?others/.test(descriptor)
      ? "third-party"
      : "titan-supplied";
  }

  return (item.supplySource ?? "titan_supplied")
    .trim()
    .toLowerCase()
    .replaceAll("_", "-");
};

const hasGenericControlKind = ({
  collection,
  item
}: CompareControlCandidate): boolean => {
  const rawKind =
    collection === "infrastructure"
      ? (item.itemKind ?? item.role ?? "infrastructure")
      : (item.role ?? item.itemKind ?? collection);

  return GENERIC_CONTROL_KINDS.has(rawKind);
};

const controlCandidateSpecificity = (candidate: CompareControlCandidate): number => {
  const kind = controlParentKindOf(candidate.collection, candidate.item);

  return (
    (isCentralScreenProduct(candidate.item) ? 20 : 0) +
    (GENERIC_CONTROL_KINDS.has(kind) ? 0 : 10) +
    (candidate.collection === "devices"
      ? 2
      : candidate.collection === "controllers"
        ? 1
        : 0)
  );
};

/**
 * Agent output can carry the same physical product as both its canonical
 * device/controller row and an infrastructure material mirror. Products/BOM
 * already reconcile those equal-SKU emissions; the estimator must do the same
 * or it double-counts the product and exposes the generic mirror as kit.
 */
const reconciledControlCandidates = (
  solution: CompareControlSolution
): CompareControlCandidate[] => {
  const candidates: CompareControlCandidate[] = [
    ...solution.controllers.map((item) => ({
      collection: "controllers" as const,
      item
    })),
    ...solution.devices.map((item) => ({ collection: "devices" as const, item })),
    ...solution.infrastructure.map((item) => ({
      collection: "infrastructure" as const,
      item
    }))
  ].filter((candidate) => !isCablingControlItem(candidate.item));
  const productGroups = new Map<string, CompareControlCandidate[]>();
  const withoutProductIdentity: CompareControlCandidate[] = [];

  for (const candidate of candidates) {
    const partNo = normalizedProductPartNo(candidate.item);

    if (!partNo) {
      withoutProductIdentity.push(candidate);
      continue;
    }

    const key = `${partNo}\u001f${normalizedSupplySource(
      candidate.collection,
      candidate.item
    )}`;
    productGroups.set(key, [...(productGroups.get(key) ?? []), candidate]);
  }

  const reconciled = [...productGroups.values()].flatMap((group) => {
    const collections = new Set(group.map((candidate) => candidate.collection));
    const duplicateEmission = collections.size > 1 || group.some(hasGenericControlKind);

    if (!duplicateEmission) {
      return group;
    }

    const preferred = [...group].sort(
      (left, right) =>
        controlCandidateSpecificity(right) - controlCandidateSpecificity(left)
    )[0]!;
    const quantityByCollection = new Map<CompareControlCollection, number>();

    for (const candidate of group) {
      const quantity =
        typeof candidate.item.quantity === "number" &&
        Number.isFinite(candidate.item.quantity)
          ? candidate.item.quantity
          : 1;
      quantityByCollection.set(
        candidate.collection,
        (quantityByCollection.get(candidate.collection) ?? 0) + quantity
      );
    }

    const quantity = Math.max(...quantityByCollection.values());

    return [
      {
        ...preferred,
        item: {
          ...preferred.item,
          quantity
        }
      }
    ];
  });

  return [...reconciled, ...withoutProductIdentity];
};

const safeId = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 72) || "row";

const columnLabel = (row: CompareTakeoffRow): string => {
  const raw = (row.rawLabel ?? row.apartmentTypeId).trim();

  return raw.length > 0 ? raw : row.apartmentTypeId;
};

const columnSub = (beds?: number): string | undefined =>
  beds === undefined ? undefined : `${beds} bed`;

// --- Builder ----------------------------------------------------------------

const jobTotalForCells = (
  cells: CompareMatrixCell[],
  homesByType: ReadonlyMap<string, number>
): number =>
  cells.reduce(
    (total, cell) => total + cell.value * (homesByType.get(cell.apartmentTypeId) ?? 0),
    0
  );

const groupFrom = (
  id: CompareGroupId,
  label: string,
  meta: string,
  rows: CompareMatrixRow[],
  columns: CompareMatrixColumn[]
): CompareMatrixGroup => {
  const totals: CompareMatrixCell[] = columns.map((column) => ({
    apartmentTypeId: column.apartmentTypeId,
    value: rows.reduce(
      (total, row) =>
        total +
        (row.cells.find((cell) => cell.apartmentTypeId === column.apartmentTypeId)
          ?.value ?? 0),
      0
    )
  }));

  return {
    id,
    label,
    meta,
    rows,
    totals,
    jobTotal: rows.reduce((total, row) => total + row.jobTotal, 0)
  };
};

const normalizedOverrides = (
  overrides: readonly CompareMatrixCellOverride[] | undefined
): ReadonlyMap<string, CompareMatrixCellOverride> => {
  const byKey = new Map<string, CompareMatrixCellOverride>();

  for (const override of overrides ?? []) {
    if (!Number.isFinite(override.value) || override.value < 0) {
      continue;
    }

    byKey.set(
      compareMatrixOverrideKey(
        override.groupId,
        override.kind,
        override.apartmentTypeId
      ),
      override
    );
  }

  return byKey;
};

const cellWithOverride = (
  overrides: ReadonlyMap<string, CompareMatrixCellOverride>,
  groupId: CompareGroupId,
  kind: string,
  apartmentTypeId: string,
  baseValue: number
): CompareMatrixCell => {
  const override = overrides.get(
    compareMatrixOverrideKey(groupId, kind, apartmentTypeId)
  );

  if (!override) {
    return {
      apartmentTypeId,
      value: baseValue
    };
  }

  return {
    apartmentTypeId,
    value: override.value,
    baseValue,
    isUserEdited: true,
    ...(override.reason ? { overrideReason: override.reason } : {}),
    ...(override.updatedAt ? { overrideUpdatedAt: override.updatedAt } : {}),
    ...(override.updatedBy ? { overrideUpdatedBy: override.updatedBy } : {})
  };
};

const formatQuantity = (value: number): string =>
  Number.isInteger(value) ? String(value) : value.toFixed(1);

const medianOf = (values: number[]): number => {
  if (values.length === 0) {
    return 0;
  }

  const sorted = [...values].sort((left, right) => left - right);
  const middle = Math.floor(sorted.length / 2);

  if (sorted.length % 2 === 1) {
    return sorted[middle] ?? 0;
  }

  return ((sorted[middle - 1] ?? 0) + (sorted[middle] ?? 0)) / 2;
};

const anomalyReason = (
  rowLabel: string,
  columnName: string,
  value: number,
  typicalValue: number,
  direction: CompareMatrixAnomaly["direction"]
): string => {
  if (direction === "only") {
    return `${columnName} is the only type showing ${formatQuantity(
      value
    )} ${rowLabel.toLowerCase()}; check that this is intentional.`;
  }

  const comparison = direction === "high" ? "higher" : "lower";

  return `${columnName} has ${formatQuantity(value)} ${rowLabel.toLowerCase()}, ${comparison} than the typical ${formatQuantity(
    typicalValue
  )} seen across the other types.`;
};

const buildAnomalies = (
  groups: CompareMatrixGroup[],
  columns: CompareMatrixColumn[]
): CompareMatrixAnomaly[] => {
  if (columns.length < 3) {
    return [];
  }

  const columnByType = new Map(
    columns.map((column) => [column.apartmentTypeId, column])
  );
  const anomalies: CompareMatrixAnomaly[] = [];

  for (const group of groups) {
    for (const row of group.rows) {
      const values = row.cells.map((cell) => cell.value);
      const max = Math.max(...values);

      if (max === 0) {
        continue;
      }

      const nonZeroCount = values.filter((value) => value > 0).length;
      const zeroCount = values.length - nonZeroCount;
      const typicalValue = medianOf(values);

      for (const cell of row.cells) {
        const column = columnByType.get(cell.apartmentTypeId);

        if (!column) {
          continue;
        }

        let direction: CompareMatrixAnomaly["direction"] | undefined;

        if (
          typicalValue > 0 &&
          cell.value >= typicalValue * 1.5 &&
          cell.value - typicalValue >= 2
        ) {
          direction = "high";
        } else if (
          typicalValue > 0 &&
          cell.value <= typicalValue * 0.5 &&
          typicalValue - cell.value >= 2
        ) {
          direction = "low";
        } else if (
          typicalValue > 0 &&
          cell.value === 0 &&
          zeroCount <= Math.max(1, Math.floor(values.length * 0.15)) &&
          nonZeroCount >= 3
        ) {
          direction = "low";
        } else if (typicalValue === 0 && cell.value > 0 && nonZeroCount === 1) {
          direction = "only";
        }

        if (!direction) {
          continue;
        }

        anomalies.push({
          id: `compare-anomaly-${safeId(row.id)}-${safeId(cell.apartmentTypeId)}`,
          groupId: group.id,
          rowId: row.id,
          rowLabel: row.label,
          apartmentTypeId: cell.apartmentTypeId,
          apartmentTypeName: column.name,
          value: cell.value,
          typicalValue,
          direction,
          reason: anomalyReason(
            row.label,
            column.name,
            cell.value,
            typicalValue,
            direction
          )
        });
      }
    }
  }

  return anomalies;
};

export const buildCompareMatrixViewModel = (
  input: BuildCompareMatrixInput
): CompareMatrixViewModel => {
  const { takeoffRows, solutions } = input;
  const overrides = normalizedOverrides(input.overrides);

  // Columns = every apartment type in the project. Take-off rows are the
  // project-level source of truth for the type list; any solution without a
  // matching take-off row is still surfaced as a column.
  const takeoffByType = new Map(takeoffRows.map((row) => [row.apartmentTypeId, row]));
  const orderedTypeIds: string[] = [];
  const seen = new Set<string>();

  for (const row of takeoffRows) {
    if (!seen.has(row.apartmentTypeId)) {
      seen.add(row.apartmentTypeId);
      orderedTypeIds.push(row.apartmentTypeId);
    }
  }

  for (const solution of solutions) {
    if (!seen.has(solution.apartmentTypeId)) {
      seen.add(solution.apartmentTypeId);
      orderedTypeIds.push(solution.apartmentTypeId);
    }
  }

  const solutionByType = new Map(
    solutions.map((solution) => [solution.apartmentTypeId, solution])
  );

  const columns: CompareMatrixColumn[] = orderedTypeIds.map((apartmentTypeId) => {
    const takeoff = takeoffByType.get(apartmentTypeId);
    const solution = solutionByType.get(apartmentTypeId);
    const homes = takeoff?.quantity ?? solution?.quantity ?? 1;
    const name = takeoff
      ? columnLabel(takeoff)
      : columnLabel({ apartmentTypeId, quantity: homes });
    const sub = columnSub(takeoff?.beds);

    return {
      apartmentTypeId,
      name,
      ...(sub ? { sub } : {}),
      homes
    };
  });

  const homesByType = new Map(
    columns.map((column) => [column.apartmentTypeId, column.homes])
  );

  // --- Take-offs group ---
  const takeoffRowsVm: CompareMatrixRow[] = [];

  for (const field of TAKEOFF_FIELDS) {
    const cells: CompareMatrixCell[] = columns.map((column) => {
      const takeoff = takeoffByType.get(column.apartmentTypeId);
      const raw = takeoff ? takeoff[field.key] : undefined;
      const value = typeof raw === "number" && Number.isFinite(raw) ? raw : 0;

      return cellWithOverride(
        overrides,
        "takeoffs",
        String(field.key),
        column.apartmentTypeId,
        value
      );
    });

    const present =
      takeoffRows.some((row) => typeof row[field.key] === "number") ||
      cells.some((cell) => cell.isUserEdited === true);
    const total = cells.reduce((sum, cell) => sum + cell.value, 0);

    if (!present || total === 0) {
      continue;
    }

    takeoffRowsVm.push({
      id: `compare-takeoff-${safeId(String(field.key))}`,
      kind: String(field.key),
      label: field.label,
      cells,
      jobTotal: jobTotalForCells(cells, homesByType)
    });
  }

  // --- Controls group ---
  // Per-type, per-kind control counts (cabling excluded entirely).
  const controlKinds = new Map<string, string>(); // kind -> label
  const countsByKind = new Map<string, Map<string, number>>(); // kind -> typeId -> count

  const tallyCollection = (
    collection: CompareControlCollection,
    apartmentTypeId: string,
    items: readonly CompareControlItem[]
  ) => {
    for (const item of items) {
      if (isCablingControlItem(item)) {
        continue;
      }

      const kind = controlParentKindOf(collection, item);

      if (!controlKinds.has(kind)) {
        controlKinds.set(kind, controlLabel(kind));
      }

      const perType = countsByKind.get(kind) ?? new Map<string, number>();
      const qty =
        typeof item.quantity === "number" && Number.isFinite(item.quantity)
          ? item.quantity
          : 1;
      perType.set(apartmentTypeId, (perType.get(apartmentTypeId) ?? 0) + qty);
      countsByKind.set(kind, perType);
    }
  };

  for (const solution of solutions) {
    for (const candidate of reconciledControlCandidates(solution)) {
      tallyCollection(candidate.collection, solution.apartmentTypeId, [candidate.item]);
    }
  }

  for (const override of input.overrides ?? []) {
    if (override.groupId !== "controls" || !seen.has(override.apartmentTypeId)) {
      continue;
    }

    if (!controlKinds.has(override.kind)) {
      controlKinds.set(override.kind, controlLabel(override.kind));
    }
  }

  const orderedKinds = [...controlKinds.keys()].sort((left, right) => {
    const leftIndex = CONTROL_ORDER.indexOf(left);
    const rightIndex = CONTROL_ORDER.indexOf(right);
    const leftRank = leftIndex === -1 ? Number.MAX_SAFE_INTEGER : leftIndex;
    const rightRank = rightIndex === -1 ? Number.MAX_SAFE_INTEGER : rightIndex;

    if (leftRank !== rightRank) {
      return leftRank - rightRank;
    }

    return (controlKinds.get(left) ?? left).localeCompare(
      controlKinds.get(right) ?? right
    );
  });

  const controlRowsVm: CompareMatrixRow[] = orderedKinds.map((kind) => {
    const perType = countsByKind.get(kind) ?? new Map<string, number>();
    const cells: CompareMatrixCell[] = columns.map((column) =>
      cellWithOverride(
        overrides,
        "controls",
        kind,
        column.apartmentTypeId,
        perType.get(column.apartmentTypeId) ?? 0
      )
    );
    const note = controlRowNote(kind);

    return {
      id: `compare-control-${safeId(kind)}`,
      kind,
      label: controlKinds.get(kind) ?? humanise(kind),
      ...(note ? { note } : {}),
      cells,
      jobTotal: jobTotalForCells(cells, homesByType)
    };
  });

  const groups: CompareMatrixGroup[] = [
    groupFrom(
      "takeoffs",
      "Take-offs",
      "what was measured in the property",
      takeoffRowsVm,
      columns
    ),
    groupFrom(
      "controls",
      "Controls",
      "the control kit we provide",
      controlRowsVm,
      columns
    )
  ];
  const anomalies = buildAnomalies(groups, columns);

  const dataGaps: string[] = [];

  if (columns.length === 0) {
    dataGaps.push("No apartment types are loaded for this project yet.");
  }

  if (takeoffRowsVm.length === 0 && columns.length > 0) {
    dataGaps.push("No measured take-off quantities are available for these types.");
  }

  if (controlRowsVm.length === 0 && columns.length > 0) {
    dataGaps.push("No generated control items are available for these types.");
  }

  const jobTotalHomes = columns.reduce((total, column) => total + column.homes, 0);

  return {
    ...(input.projectId ? { projectId: input.projectId } : {}),
    status: columns.length > 0 ? "ready" : "empty",
    columns,
    jobTotalLabel: "Job total",
    jobTotalSub: `x${jobTotalHomes} home${jobTotalHomes === 1 ? "" : "s"}`,
    groups,
    anomalies,
    dataGaps
  };
};
